
import { Position } from "@/types/locationTypes";
import { supabase } from "@/integrations/supabase/client";

// Type for the shot history record
export type ShotHistoryRecord = {
  id: string;
  user_id: string | null;
  start_position: Position;
  end_position: Position;
  distance: number;
  tendency?: 'left' | 'right' | null;
  club?: string;
  notes?: string;
  created_at: string;
};

/**
 * Saves a golf shot distance measurement to history
 * @param startPosition Start position
 * @param endPosition End position
 * @param distance Distance in meters
 * @param tendency Ball tendency (left, center, right)
 * @param club Club used for the shot
 * @param notes Observations about the shot
 * @returns Promise with the result of the save operation
 */
export const saveShotToHistory = async (
  startPosition: Position, 
  endPosition: Position, 
  distance: number,
  tendency?: 'left' | 'right' | null,
  club?: string,
  notes?: string
): Promise<{success: boolean, error: string | null}> => {
  try {
    // First check if user is authenticated
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error('User is not authenticated');
      return { success: false, error: 'Vous devez être connecté pour enregistrer un tir' };
    }
    
    const { error } = await supabase
      .from('golf_shot_history')
      .insert({
        user_id: session.session.user.id, // Always include user_id for RLS
        start_latitude: startPosition.latitude,
        start_longitude: startPosition.longitude,
        start_timestamp: new Date(startPosition.timestamp).toISOString(),
        end_latitude: endPosition.latitude,
        end_longitude: endPosition.longitude,
        end_timestamp: new Date(endPosition.timestamp).toISOString(),
        distance: distance,
        tendency: tendency || null,
        club: club || null,
        notes: notes || null
      });
      
    if (error) {
      console.error('Error saving shot to history:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, error: null };
  } catch (error: any) {
    console.error('Error saving shot to history:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetches shot history records
 * @param limit Maximum number of records to fetch
 * @returns Promise with the list of shot history records
 */
export const fetchShotHistory = async (limit: number = 10): Promise<{
  data: ShotHistoryRecord[] | null;
  error: string | null;
}> => {
  try {
    // First check if user is authenticated
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error('User is not authenticated');
      return { data: null, error: 'Vous devez être connecté pour voir votre historique' };
    }
    
    const { data, error } = await supabase
      .from('golf_shot_history')
      .select('*')
      .eq('user_id', session.session.user.id) // Filter by the current user's ID for security
      .order('created_at', { ascending: false })
      .limit(limit);
      
    if (error) {
      console.error('Error fetching shot history:', error);
      return { data: null, error: error.message };
    }
    
    // Transform the data from database format to our Position type
    const transformedData = data.map(record => ({
      id: record.id,
      user_id: record.user_id,
      start_position: {
        latitude: record.start_latitude,
        longitude: record.start_longitude,
        timestamp: new Date(record.start_timestamp).getTime()
      },
      end_position: {
        latitude: record.end_latitude,
        longitude: record.end_longitude,
        timestamp: new Date(record.end_timestamp).getTime()
      },
      distance: record.distance,
      tendency: record.tendency as 'left' | 'right' | null,
      club: record.club || undefined,
      notes: record.notes || undefined,
      created_at: record.created_at
    }));
    
    return { data: transformedData, error: null };
  } catch (error: any) {
    console.error('Error fetching shot history:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Deletes a shot history record
 * @param id ID of the record to delete
 * @returns Promise with the result of the delete operation
 */
export const deleteShotFromHistory = async (
  id: string
): Promise<{success: boolean, error: string | null}> => {
  try {
    // First check if user is authenticated
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error('User is not authenticated');
      return { success: false, error: 'Vous devez être connecté pour supprimer un tir' };
    }
    
    const { error } = await supabase
      .from('golf_shot_history')
      .delete()
      .eq('id', id)
      .eq('user_id', session.session.user.id); // Ensure we only delete records owned by this user
      
    if (error) {
      console.error('Error deleting shot from history:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, error: null };
  } catch (error: any) {
    console.error('Error deleting shot from history:', error);
    return { success: false, error: error.message };
  }
};
